import { useCallback, useSyncExternalStore } from "react";

/**
 * Per-device preferences (docs/UX.md §4 设置页).
 * Kept in localStorage and shared through a tiny store so a change on the
 * settings page reaches the record page immediately.
 */
export interface Settings {
  /** docs/UX.md §1.3 — stop 即提交, with a 3 s undo window. */
  autoSubmit: boolean;
  /** §2 快速模式 for the annotator workbench (owned by that feature). */
  fastMode: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  autoSubmit: true,
  fastMode: true,
};

const KEY = "xz.settings";

let cache: Settings | null = null;
const listeners = new Set<() => void>();

function read(): Settings {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<Settings>) : {};
    cache = { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    cache = { ...DEFAULT_SETTINGS };
  }
  return cache;
}

function emit(): void {
  for (const l of listeners) l();
}

export function getSettings(): Settings {
  return read();
}

export function setSetting<K extends keyof Settings>(key: K, value: Settings[K]): void {
  cache = { ...read(), [key]: value };
  try {
    localStorage.setItem(KEY, JSON.stringify(cache));
  } catch {
    /* private mode — the change still applies for this tab */
  }
  emit();
}

/** Test helper. */
export function resetSettings(): void {
  cache = null;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSettings(): [
  Settings,
  <K extends keyof Settings>(k: K, v: Settings[K]) => void,
] {
  const settings = useSyncExternalStore(subscribe, read, () => DEFAULT_SETTINGS);
  const set = useCallback(<K extends keyof Settings>(k: K, v: Settings[K]) => setSetting(k, v), []);
  return [settings, set];
}
